import Certipedia from 'assets/svg/certipedia/certipedia-icon.svg'
import MailIcon from 'assets/svg/mail/mail.svg'
import WhatsappIcon from 'assets/svg/whatsapp/whatsapp.svg'
import Divider from 'common/components/Divider'
import Flex from 'common/components/Flex'
import LandingFooterLogo from 'common/components/LandingFooter/LandingFooterLogo'
import SocialMedia from 'common/components/SocialMedia'
import { TSocialMediaName } from 'common/components/SocialMedia/interface'
import Typography from 'common/components/Typography'
import {
  CERTIPEDIA_URL,
  CONTACTS,
  NEWS_URL,
  WEB_DESKTOP_RALALI_URL,
} from 'common/constants'
import theme from 'common/theme'
import { useLoadedFlag } from 'lib/feature-flags'
import MediaHelper from 'lib/media-helper'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

const Footer = styled.div`
  background: ${theme.colors.grayLighter};
  justify-content: center;
  align-items: center;
  width: 100%;
`
const FooterWrapper = styled.div`
  width: 1180px;
  margin: 0 auto;
  padding: 80px 0px 48px;

  ${MediaHelper('desktop')`
    width: 100%; 
    padding: 32px 16px;
    box-sizing: border-box;
  `}
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: 308px 1fr 244px;
  column-gap: 60px;

  ${MediaHelper('tablet')`
    grid-template-columns: 1fr;
  `}
`

const AboutRalaliSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const LinkSectionWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;

  ${MediaHelper('tablet')`
    gap: 32px;
    margin: 32px 0;
  `}

  ${MediaHelper('mobile')`
    margin: 32px 0;
    grid-template-columns: repeat(2, 1fr);
    gap: 32px;
  `}
`

const StyledLink = styled.a`
  text-decoration: none;
  cursor: pointer;
  font-size: 14px;
  color: ${theme.colors.grayDark};

  :hover {
    color: ${theme.colors.redOrange};
  }
`

const RightSideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 48px;

  ${MediaHelper('tablet')`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
  `}

  ${MediaHelper('mobile')`
    display: flex;
    flex-direction: column;
    gap: 32px;
  `};
`

const DownloadLinkWrapper = styled(Flex)`
  img {
    height: 36px;
    width: 100%;
  }
`

const CopyrightWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: center;
  align-items: center;

  > div {
    margin: 48px 0;

    ${MediaHelper('tablet')`
      margin: 32px 0;
    `}
  }

  p {
    ${MediaHelper('mobile')`
      font-size: 10px;
      text-align: center;
    `}
  }
`

const LogoWrapper = styled.div``

const ContactWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const Contact = styled(Flex)`
  gap: 16px;
  font-size: 14px;
  color: ${theme.colors.grayDark};
`

interface ILinkSectionProps {
  title: string
  items: {
    label: string
    href: string
  }[]
}

const FOOTER_LINKS = [
  {
    title: 'RALALI',
    items: [
      {
        label: 'ABOUT_US',
        href: `${WEB_DESKTOP_RALALI_URL}/tentangkami`,
      },
      {
        label: 'RALALI_FOOD',
        href: `${WEB_DESKTOP_RALALI_URL}/food`,
      },
      {
        label: 'CAREERS',
        href: `${WEB_DESKTOP_RALALI_URL}/careers`,
      },
    ],
  },
  {
    title: 'BUSINESS_SOLUTIONS',
    items: [
      {
        label: 'MARKETPLACE',
        href: `${WEB_DESKTOP_RALALI_URL}/`,
      },
      {
        label: 'RALALI_TENDER',
        href: `${WEB_DESKTOP_RALALI_URL}/solutions/tender`,
      },
      {
        label: 'RALALI_PLUS',
        href: `${WEB_DESKTOP_RALALI_URL}/solutions/plus`,
      },
      {
        label: 'RALALI_DIRECT',
        href: `${WEB_DESKTOP_RALALI_URL}/solutions/direct`,
      },
      {
        label: 'RALALI_PAVILION',
        href: `${WEB_DESKTOP_RALALI_URL}/solutions/pavilion`,
      },
      {
        label: 'RALALI_AGENT',
        href: `${WEB_DESKTOP_RALALI_URL}/solutions/agent`,
      },
    ],
  },
  {
    title: 'OTHERS',
    items: [
      {
        label: 'EVENT',
        href: `${WEB_DESKTOP_RALALI_URL}/event`,
      },
      {
        label: 'NEWS',
        href: NEWS_URL,
      },
      {
        label: 'ALL_CATEGORIES',
        href: `${WEB_DESKTOP_RALALI_URL}/all-categories`,
      },
      {
        label: 'ALL_BRANDS',
        href: `${WEB_DESKTOP_RALALI_URL}/all-brands`,
      },
      {
        label: 'HELLO_RALALI',
        href: `${WEB_DESKTOP_RALALI_URL}/hubungikami`,
      },
    ],
  },
]

const SOCIAL_MEDIA: TSocialMediaName[] = [
  'Facebook',
  'Twitter',
  'Youtube',
  'LinkedIn',
  'Instagram',
]

const APP_DOWNLOAD_LINKS: TSocialMediaName[] = [
  'Google Play',
  'Apple Store',
]

function LinkSection({ items, title }: ILinkSectionProps) {
  const { t } = useTranslation(['common'])

  return (
    <Flex flexDirection="column">
      <Typography type="bh5">{t(title)}</Typography>
      <Divider
        margin="16px 0"
        height="1px"
        borderRadius={0}
        width="100%"
        color={theme.colors.grayLight}
      />
      <Flex flexDirection="column" gap="16px">
        {items.map((item) => (
          <StyledLink key={item.label} href={item.href}>
            <a>{t(item.label)}</a>
          </StyledLink>
        ))}
      </Flex>
    </Flex>
  )
}

function LandingFooter(): JSX.Element {
  const { t } = useTranslation(['common'])
  const isEnableISOCerf = useLoadedFlag(
    'biz--iso-certificate--enable--website',
  )

  return (
    <Footer data-testid="main-footer">
      <FooterWrapper>
        <Grid>
          <AboutRalaliSection>
            <LogoWrapper>
              <LandingFooterLogo />
            </LogoWrapper>
            <Typography color="grayDark" type="rsubtitle">
              {t('FOOTER_DESCRIPTION')}
            </Typography>
            <ContactWrapper>
              <Contact>
                <MailIcon />
                {CONTACTS.EMAIL}
              </Contact>
              <Contact>
                <WhatsappIcon width="22px" fill="#747474" />
                {CONTACTS.PHONE}
              </Contact>
            </ContactWrapper>
          </AboutRalaliSection>
          <LinkSectionWrapper>
            {FOOTER_LINKS.map((item) => (
              <LinkSection key={item.title} {...item} />
            ))}
          </LinkSectionWrapper>
          <RightSideWrapper>
            <Flex flexDirection="column" gap="12px">
              <Typography type="bh5">{t('FIND_US')}</Typography>
              <Flex gap="10px">
                {SOCIAL_MEDIA.map((item) => (
                  <SocialMedia key={item} name={item} />
                ))}
              </Flex>
            </Flex>
            {isEnableISOCerf && (
              <Flex flexDirection="column" gap="12px">
                <Typography type="bh5">
                  {t('SECURITY_N_PRIVACY')}
                </Typography>
                <a
                  rel="noreferrer"
                  target="_blank"
                  href={CERTIPEDIA_URL}
                >
                  <Certipedia />
                </a>
              </Flex>
            )}
            <DownloadLinkWrapper gap="10px">
              {APP_DOWNLOAD_LINKS.map((item) => (
                <SocialMedia key={item} name={item} />
              ))}
            </DownloadLinkWrapper>
          </RightSideWrapper>
        </Grid>
        <CopyrightWrapper>
          <Divider
            height="1px"
            borderRadius={0}
            width="100%"
            color={theme.colors.grayLight}
          />
          <Typography type="rsubtitle" color="grayDark">
            {`Copyright © ${new Date().getFullYear()} PT. Raksasa Laju Lintang (ralali.com).
              All Rights Reserved`}
          </Typography>
        </CopyrightWrapper>
      </FooterWrapper>
    </Footer>
  )
}

export default LandingFooter
